import { convertQueryObjToString, mapQueryStringToFilterObject } from '@components-web/filters/helper';
import SortDropdown from '@components-web/sort-dropdown';
import { pageSizeOptions } from '@constants/generic-constants';
import { usePagination } from '@hooks';
import { isScrollEnd } from '@utils/utils';
import { Table as AntdTable, Flex } from 'antd';
import React from 'react';
import { BsInfoCircle } from 'react-icons/bs';
import { useLocation, useNavigate } from 'react-router-dom';
import { TableSkeleton, Tooltip } from '../index';

const Table = ({
  columns = [],
  dataSource = [],
  onRowClick = () => { },
  rowSelection = null,
  loading = false,
  loaderRowCount = 10,
  size = 'large',
  bordered = false,
  pagination = false,
  paginationPosition = ['bottomRight'],
  className = '',
  Footer = null,
  scrollX = 'max-content',
  rowClassName = () => { },
  scrollY = undefined,
  onScrollEnd = () => { },
  showListingRange = false,
  stickyHeader = true,
  pageSize = 20,
  setPageSize = () => { },
  customSortOptions = [],
  expandable,
  offsetHeader = -24,
  showSorterTooltip = true,
  hideLastPage = false,
}) => {
  const paginationData = usePagination({ pagination });
  const { currentPage, handlePageChange, shouldPaginate } = pagination !== false ? paginationData : {};

  const location = useLocation();
  const navigate = useNavigate();
  const queryObj = mapQueryStringToFilterObject(location.search).queryObj;

  const updateSortInQueryParams = (field, order) => {
    const updatedQuery = { ...queryObj };

    Object.keys(updatedQuery).forEach((key) => {
      if (key.startsWith('sort[')) {
        delete updatedQuery[key];
      }
    });

    updatedQuery[`sort[${field}]`] = order;

    navigate({
      pathname: location.pathname,
      search: `?${convertQueryObjToString(updatedQuery)}`,
    });
  };

  if (loading) {
    return <TableSkeleton columns={columns} rowCount={loaderRowCount} size={size} />;
  }

  const firstListingNumber = ((currentPage || 1) - 1) * pageSize + 1;
  const maxLastListingNumber = firstListingNumber - 1 + (dataSource?.length || pageSize);
  const totalCount = pagination?.total_count ?? dataSource?.length;
  const actualLastListingNumber = maxLastListingNumber > totalCount ? totalCount : maxLastListingNumber;

  return (
    <>
      <Flex align={'center'} justify={'space-between'}>
        {showListingRange && dataSource?.length > 0 && (
          <span className="fw-600" style={{ marginBottom: -10 }}>
            {`Showing ${firstListingNumber?.toLocaleString()} - ${actualLastListingNumber?.toLocaleString()} of ${totalCount?.toLocaleString()} results`}
          </span>
        )}
        {!!customSortOptions?.length && <SortDropdown sortOptions={customSortOptions} />}
      </Flex>
      <AntdTable
        {...(stickyHeader
          ? {
            sticky: { offsetHeader },
          }
          : {})}
        dataSource={dataSource}
        rowKey={(record) => record.ID ?? record?.key ?? record?.SKU}
        pagination={
          shouldPaginate && pagination
            ? {
              current: currentPage,
              pageSize: pageSize,
              total: pagination?.total_count || 0,
              onChange: (page, size) => handlePageChange({ page, size, setPageSize }),
              showSizeChanger: true,
              pageSizeOptions: pageSizeOptions,
              position: paginationPosition,

              itemRender: (current, type, originalElement) => {
                if (hideLastPage) {
                  const totalCount = pagination?.total_count || 0;
                  const lastPage = Math.ceil(totalCount / pageSize);

                  if (type === 'page' && current === lastPage) {
                    return null;
                  }
                }

                return originalElement;
              },
            }
            : false
        }
        onChange={(pagination, __, sorter) => {
          const columnDef = sorter?.column?.sortKey;
          const sortField = columnDef || sorter?.field;

          if (sorter?.order) {
            updateSortInQueryParams(sortField, sorter.order === 'ascend' ? 'asc' : 'desc');
          }
          if (pagination?.current && shouldPaginate && pagination?.current !== currentPage) {
            handlePageChange({ page: pagination.current, size: pagination.pageSize, setPageSize });
          }
        }}
        onRow={(record, rowIndex) => ({
          onClick: () => onRowClick(record, rowIndex),
        })}
        rowSelection={rowSelection}
        size={size}
        bordered={bordered}
        scroll={{ x: scrollX, y: scrollY }}
        onScroll={(e) => {
          if (isScrollEnd(e)) {
            onScrollEnd();
          }
        }}
        columns={(() => {
          const renderColumns = (cols) => cols.map((column) => {
            const sortField = column.sortKey || column.dataIndex;
            const sortFieldKey = `sort[${sortField}]`;
            const sortOrder = queryObj[sortFieldKey] ? (queryObj[sortFieldKey] === 'asc' ? 'ascend' : 'descend') : null;
            return {
              title: column.description ? (
                <Flex align="center">
                  <span>{typeof column.title === 'string' ? column.title?.toUpperCase() : column?.title}</span>
                  <Tooltip title={column.description}>
                    <BsInfoCircle
                      size={14}
                      color="var(--gray-500)"
                      className={'flexShrink0'}
                      style={{
                        cursor: 'pointer',
                        marginLeft: 5,
                      }}
                    />
                  </Tooltip>
                </Flex>
              ) : (
                typeof column?.title === 'string' ? column?.title?.toUpperCase() : column?.title
              ),
              dataIndex: column?.dataIndex,
              width: column?.width,
              className: column?.className || '',
              align: column?.align || 'left',
              fixed: column?.fixed,
              sorter: !!column?.sortKey,
              sortOrder: sortOrder,
              sortKey: column?.sortKey,
              sortDirections: ['ascend', 'descend', 'ascend'],
              children: column.children ? renderColumns(column.children) : undefined,
              onHeaderCell: column?.onHeaderCell,
              render: column?.render,
            };
          });
          return renderColumns(columns);
        })()}
        className={className}
        {...(Footer ? { footer: Footer } : {})}
        rowClassName={(record, index) => {
          return rowClassName(dataSource[index], record, index);
        }}
        expandable={expandable}
        showSorterTooltip={showSorterTooltip}
      />
    </>
  );
};

export default Table;
